// =================================================================
// Welcome page
//
// The first page a Hexnode-managed tablet sees when its kiosk web
// app is tapped. The Hexnode policy points at:
//
//   https://brain.quiltt.com/welcome.html?did=%udid%
//
// Hexnode substitutes %udid% with the device's UDID before the URL
// reaches the browser. So when this page mounts, the query string
// contains a real UDID.
//
// Three outcomes from here:
//
//   1. UDID is paired to a resident (device_index/{udid} exists)
//      → sign the resident in via useResident.signInByDevice
//      → redirect to /resident (the resident's dashboard)
//
//   2. UDID is NOT paired (no device_index entry)
//      → generate an ephemeral 6-character pairing code
//      → store it at device_pairing/{code} with the UDID and an
//        expiration timestamp
//      → display the SHORT pairing code on the tablet
//      → admin reads the short code (not the long UDID), types it
//        into the resident roster to pair this tablet to the
//        resident, which writes device_udid on the account AND
//        device_index/{udid} → accountId
//      → on the next tablet refresh, outcome #1 applies
//
//   3. No ?did= param at all (someone visits /welcome.html in a
//      regular browser without the Hexnode-injected UDID)
//      → show a "this page is for device-bundled tablets, please
//        use brain.quiltt.com for normal sign-in" message
//
// Why a short pairing code instead of the UDID: residents can't
// easily copy a 16-character hex string off the tablet and read
// it accurately to the admin. A 6-character code from an
// unambiguous uppercase alphabet is achievable. The code is
// ephemeral (4-hour expiration) and is consumed when used.
//
// Alphabet note: uppercase-only and excludes ambiguous chars (no
// O / 0, I / 1, L, S / 5, B / 8). The admin input on the resident
// roster force-uppercases, so any lowercase chars would be
// unkeyable by the admin. Keeping the alphabet uppercase-only
// means the code on the tablet matches what the admin types,
// char-for-char.
//
// The Welcome page lives OUTSIDE the Layout wrapper in App.jsx so
// the masthead doesn't appear here — this is a full-screen kiosk
// page, not a normal app page.
// =================================================================

import { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ref,
  get,
  set,
  onValue,
  serverTimestamp,
} from 'firebase/database';
import { db } from '../lib/firebase.js';
import { useResident } from '../lib/useResident.jsx';
import './Welcome.css';

// Validation: Android UDIDs from Hexnode are 16 lowercase hex chars.
// Be a bit lenient on case in case Hexnode ever changes display
// format, but reject anything that doesn't look like an SSAID-style
// identifier so we don't accept arbitrary garbage from the URL.
const UDID_PATTERN = /^[0-9a-fA-F]{16}$/;

// Ephemeral pairing code alphabet — uppercase only, no ambiguous
// chars. Excludes:
//   O 0     — look identical
//   I 1 L   — look identical
//   S 5     — look similar in many fonts
//   B 8     — look similar at a glance
// Remaining: 27 chars × 6 positions = ~387 million combinations.
// At our scale (a handful of active codes at any moment) collision
// probability is essentially zero, and we have an expiration window
// to clean up any stale ones.
const PAIRING_ALPHABET = '234679ACDEFGHJKMNPQRTUVWXYZ';
const PAIRING_CODE_LENGTH = 6;
const PAIRING_CODE_TTL_MS = 4 * 60 * 60 * 1000; // 4 hours

// Generate a pairing code using crypto.getRandomValues for actual
// randomness (not Math.random, which could lead to collisions in
// edge cases).
function generatePairingCode() {
  const buf = new Uint32Array(PAIRING_CODE_LENGTH);
  crypto.getRandomValues(buf);
  let code = '';
  for (let i = 0; i < PAIRING_CODE_LENGTH; i++) {
    code += PAIRING_ALPHABET[buf[i] % PAIRING_ALPHABET.length];
  }
  return code;
}

export default function Welcome() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { signInByDevice, isSignedIn } = useResident();

  // The raw UDID read once at mount. We don't want this to change
  // mid-flow even if the URL somehow does. Read synchronously to
  // avoid a render with no UDID before we know what's going on.
  const udidRef = useRef(searchParams.get('did') || '');
  const udid = udidRef.current;

  // Phase machine:
  //   'checking'    — looking up UDID in device_index
  //   'signing-in'  — UDID matched, calling signInByDevice
  //   'unpaired'    — UDID didn't match, showing pairing code
  //   'no-udid'     — no ?did= param, showing a "use brain.quiltt.com" message
  //   'invalid'     — ?did= present but malformed
  //   'error'       — Firebase read failed
  const [phase, setPhase] = useState('checking');
  const [pairingCode, setPairingCode] = useState(null);
  const [codeRevealed, setCodeRevealed] = useState(false);
  const [error, setError] = useState(null);

  // If already signed in (e.g., user already paired and is just
  // hitting refresh), bounce straight to the dashboard. Don't
  // re-process the UDID — that's wasted work and could cause a
  // flicker.
  useEffect(() => {
    if (isSignedIn) {
      navigate('/resident', { replace: true });
    }
  }, [isSignedIn, navigate]);

  // Main flow. Subscribes to device_index/{udid} so the page reacts
  // the moment the admin pairs this tablet — no manual refresh
  // needed. If the index entry doesn't exist yet, we kick off the
  // pairing-code generation in parallel, but keep the subscription
  // active so that when the admin's "Pair" click lands and the
  // index entry appears, this effect fires again and signs the
  // resident in automatically.
  useEffect(() => {
    if (isSignedIn) return; // handled by the redirect effect above

    // No UDID at all — somebody visited /welcome.html directly
    // without the Hexnode-injected param. Probably an admin
    // typing it in to test, or a developer poking around.
    if (!udid) {
      setPhase('no-udid');
      return;
    }

    // UDID present but doesn't match the expected shape. Could be
    // a future Hexnode change, a malformed substitution, or a
    // human typing nonsense. Don't try to look it up.
    if (!UDID_PATTERN.test(udid)) {
      setPhase('invalid');
      return;
    }

    let cancelled = false;
    let pairingCodeRequested = false;

    // Subscribe to device_index for THIS udid. The subscription
    // lives until the component unmounts (or until the UDID changes,
    // which doesn't happen in practice — udid is stable via the
    // useRef at mount).
    //
    // The callback fires on EVERY change — initial value, plus any
    // future writes by an admin who pairs (or unpairs) this device.
    // That's what gives us the "no refresh needed" behavior: as
    // soon as the admin's pair-click writes device_index/{udid},
    // this callback runs and we sign the resident in.
    const indexRef = ref(db, `brain_health/device_index/${udid}`);
    const unsubscribe = onValue(
      indexRef,
      (snap) => {
        if (cancelled) return;

        if (snap.exists()) {
          const indexEntry = snap.val();
          const accountId = indexEntry?.accountId;
          if (accountId) {
            setPhase('signing-in');
            signInByDevice(accountId);
            // The redirect-when-signed-in effect above will fire
            // once useResident's onValue subscription resolves.
            return;
          }
          // Malformed entry — treat as unpaired and fall through
          // to the pairing-code branch below.
        }

        // No index entry (or malformed). Show the pairing screen.
        // ensurePairingCode is idempotent in the sense that it
        // reuses an existing valid code rather than churning new
        // ones, but we still only want to call it once per mount
        // to avoid a write race if the subscription fires twice
        // in quick succession.
        if (!pairingCodeRequested) {
          pairingCodeRequested = true;
          ensurePairingCode().catch((err) => {
            if (cancelled) return;
            console.error('Pairing code generation failed:', err);
            setError(err.message || 'Something went wrong.');
            setPhase('error');
          });
        }
      },
      (err) => {
        if (cancelled) return;
        console.error('device_index subscription failed:', err);
        setError(err.message || 'Something went wrong.');
        setPhase('error');
      }
    );

    // If a recent pairing code already exists for this UDID, reuse
    // it (the admin might be mid-pairing and we don't want to
    // change the displayed code if they refresh the tablet). Stale
    // codes (>TTL old) are treated as nonexistent.
    async function ensurePairingCode() {
      // The pairing codes are stored at device_pairing/{code}, keyed
      // BY code (so the admin can look up by code). To find an
      // existing code for THIS udid, we'd need a reverse index.
      // We have one: device_pairing_by_udid/{udid} → {code,
      // expires_at}. Reading that is cheap and avoids generating
      // a fresh code on every refresh.
      const existingSnap = await get(
        ref(db, `brain_health/device_pairing_by_udid/${udid}`)
      );
      const existing = existingSnap.val();
      if (
        existing &&
        existing.code &&
        existing.expires_at &&
        existing.expires_at > Date.now()
      ) {
        // Still valid — reuse it.
        setPairingCode(existing.code);
        setPhase('unpaired');
        return;
      }

      // Generate a fresh code. The probability of collision in
      // 6 chars from a 24-char alphabet is negligible at our scale,
      // so we don't loop-and-check.
      const code = generatePairingCode();
      const expiresAt = Date.now() + PAIRING_CODE_TTL_MS;
      const payload = {
        udid,
        code,
        expires_at: expiresAt,
        created_at: serverTimestamp(),
      };

      // Write both lookup directions atomically would be nicer, but
      // these are two child paths; we just write them sequentially.
      // Worst case if the second write fails: the admin sees a
      // pairing code that doesn't resolve, retries, gets a new
      // working one. Tolerable failure mode.
      await set(ref(db, `brain_health/device_pairing/${code}`), payload);
      await set(
        ref(db, `brain_health/device_pairing_by_udid/${udid}`),
        { code, expires_at: expiresAt }
      );

      if (cancelled) return;
      setPairingCode(code);
      setPhase('unpaired');
    }

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [udid, isSignedIn, signInByDevice]);

  // ----- RENDER PHASES -----

  if (phase === 'checking' || phase === 'signing-in') {
    return (
      <div className="welcome-page welcome-loading">
        <p className="welcome-loading-text">
          {phase === 'signing-in' ? 'Welcome back…' : 'Loading…'}
        </p>
      </div>
    );
  }

  if (phase === 'no-udid' || phase === 'invalid') {
    return (
      <div className="welcome-page">
        <div className="welcome-wrap">
          <span className="welcome-eyebrow">Games by Quiltt</span>
          <h1 className="welcome-title">
            This page is for <em>tablets</em>.
          </h1>
          <p className="welcome-lede">
            If you're using a community-issued tablet, please ask your
            community administrator to set it up.
          </p>
          <p className="welcome-lede">
            If you're signing in from another device, please go to{' '}
            <a href="https://brain.quiltt.com" className="welcome-link">
              brain.quiltt.com
            </a>{' '}
            and sign in with your resident code.
          </p>
        </div>
      </div>
    );
  }

  if (phase === 'error') {
    return (
      <div className="welcome-page">
        <div className="welcome-wrap">
          <span className="welcome-eyebrow">Games by Quiltt</span>
          <h1 className="welcome-title">
            Something went <em>wrong</em>.
          </h1>
          <p className="welcome-lede">
            {error || 'We couldn\'t set up this tablet. Please try again, or ask your community administrator for help.'}
          </p>
          <button
            type="button"
            className="welcome-retry"
            onClick={() => window.location.reload()}
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  // phase === 'unpaired'
  //
  // Two stages on this screen, controlled by codeRevealed:
  //   1. Intro: tells the resident what Games by Quiltt is, with a
  //      "Show setup code" button.
  //   2. Code: shows the pairing code for the admin, with a brief
  //      instruction.
  //
  // The button gives the resident a moment to read about what
  // they're looking at before being faced with a 6-character
  // alphanumeric code that means nothing to them. The admin can
  // tap the button (or the resident can) when they're ready.
  return (
    <div className="welcome-page">
      <div className="welcome-wrap">
        <span className="welcome-eyebrow">Welcome to</span>
        <h1 className="welcome-title">
          Games by <em>Quiltt</em>
        </h1>
        <p className="welcome-tagline">Play sharp.</p>

        {!codeRevealed ? (
          <>
            <p className="welcome-lede">
              Games by Quiltt is a collection of brain health games
              designed with Dr. Rob Winningham, one of the leading
              voices in brain health and aging. The games are fun
              first, and they're built to support memory, attention,
              focus, and the other cognitive abilities that keep you
              sharp.
            </p>
            <p className="welcome-lede">
              Your tablet is set up for you to play any time. A few
              short sessions a week is all it takes.
            </p>
            <button
              type="button"
              className="btn welcome-reveal"
              onClick={() => setCodeRevealed(true)}
            >
              Show setup code
            </button>
          </>
        ) : (
          <>
            <p className="welcome-lede">
              One more step. Show this code to your community
              administrator so they can set up your tablet:
            </p>

            <div
              className="welcome-code"
              aria-label={`Setup code: ${pairingCode}`}
            >
              {pairingCode}
            </div>

            <p className="welcome-fine">
              Once your tablet is set up, it will sign you in
              automatically every time, without needing the code
              again.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
